import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import http from "../../utils/http";

const baseUrl = process.env.REACT_APP_API_SIDEKICK_BASE_URL;

const initialState = {
  isLoading: false,
  isDetailLoading: false,
  isMarkingPaid: false,
  isPayingStripe: false,
  creators: [],
  meta: {
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: 20,
  },
  selectedCreator: null,
  error: null,
};

export const fetchManualCreatorPayouts = createAsyncThunk(
  "manualPayouts/fetchList",
  async ({ search = "", page = 1, limit = 20 } = {}, thunkAPI) => {
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      params.set("page", String(page));
      params.set("limit", String(limit));

      const response = await http.get(
        `${baseUrl}/manual-creator-payouts?${params.toString()}`
      );
      return thunkAPI.fulfillWithValue(response?.data);
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data || { message: "Failed to load manual payouts" }
      );
    }
  }
);

export const fetchManualCreatorPayoutDetails = createAsyncThunk(
  "manualPayouts/fetchDetails",
  async (userId, thunkAPI) => {
    try {
      const response = await http.get(
        `${baseUrl}/manual-creator-payouts/${userId}`
      );
      return thunkAPI.fulfillWithValue(response?.data);
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data || { message: "Failed to load creator details" }
      );
    }
  }
);

export const markManualCreatorPayoutPaid = createAsyncThunk(
  "manualPayouts/markPaid",
  async ({ userId, amount, paymentReference, adminNote }, thunkAPI) => {
    try {
      const response = await http.post(
        `${baseUrl}/manual-creator-payouts/${userId}/mark-paid`,
        { amount, paymentReference, adminNote }
      );
      return thunkAPI.fulfillWithValue(response?.data);
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data || { message: "Failed to mark payout as paid" }
      );
    }
  }
);

export const payManualCreatorPayoutStripe = createAsyncThunk(
  "manualPayouts/payStripe",
  async ({ userId, amount, adminNote }, thunkAPI) => {
    try {
      const response = await http.post(
        `${baseUrl}/manual-creator-payouts/${userId}/pay`,
        { amount, adminNote }
      );
      return thunkAPI.fulfillWithValue(response?.data);
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data || { message: "Failed to pay via Stripe" }
      );
    }
  }
);

const manualPayoutsSlice = createSlice({
  name: "manualPayouts",
  initialState,
  reducers: {
    clearSelectedCreator(state) {
      state.selectedCreator = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchManualCreatorPayouts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchManualCreatorPayouts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.creators = action.payload?.data || [];
        state.meta = action.payload?.meta || initialState.meta;
      })
      .addCase(fetchManualCreatorPayouts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchManualCreatorPayoutDetails.pending, (state) => {
        state.isDetailLoading = true;
      })
      .addCase(fetchManualCreatorPayoutDetails.fulfilled, (state, action) => {
        state.isDetailLoading = false;
        state.selectedCreator = action.payload?.data || null;
      })
      .addCase(fetchManualCreatorPayoutDetails.rejected, (state, action) => {
        state.isDetailLoading = false;
        state.error = action.payload;
      })
      .addCase(markManualCreatorPayoutPaid.pending, (state) => {
        state.isMarkingPaid = true;
      })
      .addCase(markManualCreatorPayoutPaid.fulfilled, (state) => {
        state.isMarkingPaid = false;
      })
      .addCase(markManualCreatorPayoutPaid.rejected, (state) => {
        state.isMarkingPaid = false;
      })
      .addCase(payManualCreatorPayoutStripe.pending, (state) => {
        state.isPayingStripe = true;
      })
      .addCase(payManualCreatorPayoutStripe.fulfilled, (state) => {
        state.isPayingStripe = false;
      })
      .addCase(payManualCreatorPayoutStripe.rejected, (state) => {
        state.isPayingStripe = false;
      });
  },
});

export const { clearSelectedCreator } = manualPayoutsSlice.actions;
export default manualPayoutsSlice.reducer;
